import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  CircleUserRound,
  Clock3,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AdminStatistics as AdminStatisticsData, AdminStatisticsSummary, fetchAdminStatistics } from '../services/admin.api';
import { useTranslations } from '../translations';
import './AdminStatistics.css';

type StatisticsView = 'overview' | 'products' | 'users';
type StatisticsPeriod = 7 | 30 | 90 | 365;

const numberFormatter = new Intl.NumberFormat('ro-RO');

interface MetricCardProps {
  label: string;
  value: number;
  icon: React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  tone?: 'primary' | 'secondary';
  hint?: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon: Icon, tone = 'primary', hint }) => (
  <article className={`admin-stat-metric admin-stat-metric-${tone}`}>
    <span className="admin-stat-metric-icon"><Icon size={20} aria-hidden /></span>
    <div>
      <span className="admin-stat-metric-label">{label}</span>
      <strong>{numberFormatter.format(value)}</strong>
      {hint && <div className="admin-stat-metric-hint">{hint}</div>}
    </div>
  </article>
);

interface BreakdownProps {
  title: string;
  items: Array<{ label: string; value: number; color: string }>;
}

const BreakdownCard: React.FC<BreakdownProps> = ({ title, items }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  return (
    <article className="admin-stat-breakdown">
      <h3>{title}</h3>
      <div className="admin-stat-breakdown-list">
        {items.map((item) => {
          const percentage = total ? Math.round((item.value / total) * 100) : 0;
          return (
            <div className="admin-stat-breakdown-item" key={item.label}>
              <div className="admin-stat-breakdown-heading">
                <span><i style={{ background: item.color }} />{item.label}</span>
                <strong>{numberFormatter.format(item.value)} <small>{percentage}%</small></strong>
              </div>
              <div className="admin-stat-progress" aria-hidden>
                <span style={{ width: `${percentage}%`, background: item.color }} />
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
};

const AdminStatistics: React.FC = () => {
  const t = useTranslations();
  const labels = t.administration.statistics;
  const [view, setView] = useState<StatisticsView>('overview');
  const [period, setPeriod] = useState<StatisticsPeriod>(30);
  const [statistics, setStatistics] = useState<AdminStatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    fetchAdminStatistics(period)
      .then((data) => active && setStatistics(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [period, reloadKey]);

  const chartData = useMemo(() => statistics?.timeline.map((item) => ({
    ...item,
    label: new Intl.DateTimeFormat('ro-RO', {
      day: '2-digit',
      month: period === 365 ? 'short' : '2-digit',
    }).format(new Date(`${item.date}T12:00:00`)),
  })) ?? [], [statistics, period]);

  const growthHint = (summary: AdminStatisticsSummary) => {
    const positive = summary.growthPercentage >= 0;
    if (summary.growthPercentage === 0) return <span>{labels.noPeriodChange}</span>;
    return (
      <span className={positive ? 'positive' : 'negative'}>
        {positive ? <ArrowUpRight size={14} aria-hidden /> : <ArrowDownRight size={14} aria-hidden />}
        {Math.abs(summary.growthPercentage).toLocaleString('ro-RO')}% {labels.periodGrowth}
      </span>
    );
  };

  const summaryCards = (summary: AdminStatisticsSummary, entity: 'products' | 'users') => {
    const isProducts = entity === 'products';
    return (
      <div className="admin-stat-metrics">
        <MetricCard
          label={isProducts ? labels.totalProducts : labels.totalUsers}
          value={summary.total}
          icon={isProducts ? PackageCheck : CircleUserRound}
          tone={isProducts ? 'primary' : 'secondary'}
          hint={growthHint(summary)}
        />
        <MetricCard label={labels.newToday} value={summary.today} icon={Sparkles} />
        <MetricCard label={labels.newThisWeek} value={summary.thisWeek} icon={CalendarDays} />
        <MetricCard label={labels.newThisMonth} value={summary.thisMonth} icon={Clock3} />
      </div>
    );
  };

  if (loading && !statistics) {
    return <div className="admin-stat-state"><span className="admin-stat-loader" />{labels.loading}</div>;
  }

  if (error && !statistics) {
    return (
      <div className="admin-stat-state admin-stat-error">
        <p>{labels.loadError}</p>
        <button type="button" onClick={() => setReloadKey((key) => key + 1)}>
          <RefreshCw size={17} aria-hidden /> {labels.retry}
        </button>
      </div>
    );
  }

  if (!statistics) return null;

  const productStatusItems = [
    { label: labels.active, value: statistics.productBreakdown.active, color: '#267200' },
    { label: labels.pending, value: statistics.productBreakdown.pending, color: '#d99400' },
    { label: labels.declined, value: statistics.productBreakdown.declined, color: '#b23a2b' },
    { label: labels.expired, value: statistics.productBreakdown.expired, color: '#71806d' },
  ];
  const productTypeItems = [
    { label: labels.forSale, value: statistics.productBreakdown.forSale, color: '#267200' },
    { label: labels.wanted, value: statistics.productBreakdown.wanted, color: '#6b8e23' },
  ];
  const userStatusItems = [
    { label: labels.active, value: statistics.userBreakdown.active, color: '#267200' },
    { label: labels.blocked, value: statistics.userBreakdown.blocked, color: '#b23a2b' },
  ];
  const verificationItems = [
    { label: labels.verified, value: statistics.userBreakdown.verified, color: '#267200' },
    { label: labels.unverified, value: statistics.userBreakdown.unverified, color: '#d99400' },
  ];

  return (
    <section className="admin-statistics">
      <div className="admin-stat-toolbar">
        <div>
          <span className="admin-stat-kicker"><ShieldCheck size={16} aria-hidden /> {labels.title}</span>
          <p>{labels.description}</p>
        </div>
        <div className="admin-stat-period" aria-label={labels.periodLabel}>
          {([
            [7, labels.last7Days], [30, labels.last30Days], [90, labels.last90Days], [365, labels.lastYear],
          ] as Array<[StatisticsPeriod, string]>).map(([value, label]) => (
            <button key={value} type="button" className={period === value ? 'active' : ''} onClick={() => setPeriod(value)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-stat-view-tabs" role="tablist" aria-label={labels.title}>
        {([
          ['overview', labels.overview], ['products', labels.products], ['users', labels.users],
        ] as Array<[StatisticsView, string]>).map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={view === value} className={view === value ? 'active' : ''} onClick={() => setView(value)}>
            {label}
          </button>
        ))}
      </div>

      {(view === 'overview' || view === 'products') && summaryCards(statistics.products, 'products')}
      {view === 'overview' && <div className="admin-stat-section-divider" />}
      {(view === 'overview' || view === 'users') && summaryCards(statistics.users, 'users')}

      <article className="admin-stat-chart-card">
        <div className="admin-stat-card-heading">
          <div><h3>{labels.trendTitle}</h3><p>{labels.trendDescription}</p></div>
          {loading && <RefreshCw className="admin-stat-refreshing" size={18} aria-label={labels.loading} />}
        </div>
        <div className="admin-stat-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} accessibilityLayer margin={{ top: 10, right: 8, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="adminProductsArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#267200" stopOpacity={0.24} /><stop offset="100%" stopColor="#267200" stopOpacity={0.02} /></linearGradient>
                <linearGradient id="adminUsersArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7b9f35" stopOpacity={0.2} /><stop offset="100%" stopColor="#7b9f35" stopOpacity={0.02} /></linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#e0e8dc" strokeDasharray="4 4" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#687364', fontSize: 11 }} minTickGap={28} />
              <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: '#687364', fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #dbe5d6', boxShadow: '0 10px 30px rgba(30, 61, 20, .12)' }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
              {view !== 'users' && <Area type="monotone" dataKey="products" name={labels.productsLegend} stroke="#267200" fill="url(#adminProductsArea)" strokeWidth={2.5} />}
              {view !== 'products' && <Area type="monotone" dataKey="users" name={labels.usersLegend} stroke="#7b9f35" fill="url(#adminUsersArea)" strokeWidth={2.5} />}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </article>

      <div className="admin-stat-breakdowns">
        {(view === 'overview' || view === 'products') && <BreakdownCard title={labels.productHealth} items={productStatusItems} />}
        {(view === 'overview' || view === 'products') && <BreakdownCard title={labels.productMix} items={productTypeItems} />}
        {(view === 'overview' || view === 'users') && <BreakdownCard title={labels.userHealth} items={userStatusItems} />}
        {(view === 'overview' || view === 'users') && <BreakdownCard title={labels.verification} items={verificationItems} />}
      </div>

      <p className="admin-stat-updated"><CheckCircle2 size={14} aria-hidden /> {labels.generatedAt.replace('{date}', new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(statistics.generatedAt)))}</p>
    </section>
  );
};

export default AdminStatistics;
